import {useCallback, useEffect, useRef, useState} from 'react';

import {AnimatePresence, animate} from '@sail/engine';
import {view} from '@sail/ui';
import {captureException, captureMessage} from '@sentry/browser';
import {TextRow} from '@stripe-internal/conversations-ui';
import {
  useAnalytics,
  InlineDatumProps,
  InlineDataServiceResponseError,
  useInlineDataEvents,
} from '@stripe-internal/inline-data';
import {FormattedMessage, defineMessages} from 'react-intl';

import {RequestACallForm, RACClientErrors} from './RequestACallForm';
import {useTransitionElements} from './useTransitionElements';

const ANIMATION_DURATION = 350;

const messages = defineMessages({
  requestACallInstructionsMessage: {
    id: 'mse_inline_data.universal_help_channel_menu.rac.requestACallInstructionsMessage',
    description:
      'Instructions shown to the user in the conversation log before they are shown the RAC form.',
    defaultMessage:
      'Sure. Please provide your contact details and an agent will give you a call.',
  },
  requestACallExplanationMessage: {
    id: 'mse_inline_data.universal_help_channel_menu.rac.requestACallExplanationMessage',
    description:
      'Instructions shown to the user in the conversation log after they successfully submit a RAC request',
    defaultMessage:
      'We’re on it. You should receive a call at the number listed in the card above in the next 5 minutes. The agent will provide you with an identification code, and then ask you for your code shown above. Keep this chat open until you receive the call.',
  },
});

export interface RequestACallForm {
  name: string;
  phone_number: string;
  message: string;
}

export interface Props {
  action: InlineDatumProps['action'];
  nonce: string;
  initialName: string;
  initialPhoneNumber: string;
  requestACallForm?: RequestACallForm;
  isSectionVisible: boolean;
  isRacFormSubmitted: boolean;
  clientError?: RACClientErrors;
  serverError?: InlineDataServiceResponseError;
  handleCancel: () => void;
}

export function RequestACallSection({
  action,
  nonce,
  initialName,
  initialPhoneNumber,
  requestACallForm,
  isSectionVisible,
  isRacFormSubmitted,
  handleCancel,
  clientError,
  serverError,
}: Props) {
  const mostRecentClientError = useRef<RACClientErrors | undefined>();
  const hasLoggedClientError = useRef(false);

  const mostRecentServerError = useRef<
    InlineDataServiceResponseError | undefined
  >();
  const hasLoggedServerError = useRef(false);

  const [submitting, setSubmitting] = useState<boolean>(false);
  // Use state to track the first time we see the form is submitted to avoid animation thrash
  const [isFormSubmitted, setIsFormSubmitted] =
    useState<boolean>(isRacFormSubmitted);

  const [formData, setFormData] = useState<RequestACallForm>(
    requestACallForm ?? {
      name: initialName,
      phone_number: initialPhoneNumber,
      message: '',
    },
  );

  const analytics = useAnalytics();
  const events = useInlineDataEvents();
  const {
    showInstructions,
    showForm,
    showSummary,
    showExplanation,
    setShowInstructions,
    setShowForm,
  } = useTransitionElements({
    isSectionVisible,
    isFormSubmitted,
  });

  useEffect(() => {
    if (isRacFormSubmitted) {
      setSubmitting(false);
      setIsFormSubmitted(true);
    }
  }, [isRacFormSubmitted]);

  // The inline data polls for updates and the error is only sent on submit; not poll.
  // Keep track of the most recent error and only clear on submit instead of clearing on each poll.
  useEffect(() => {
    if (clientError && !hasLoggedClientError.current) {
      analytics.track('support_conversations_rac_client_error', {
        universalHelp: true,
      });
      captureMessage('RAC submission error', {
        level: 'warning',
        extra: {
          responseType: clientError,
        },
      });
      mostRecentClientError.current = clientError;
      hasLoggedClientError.current = true;
    }

    if (clientError) {
      setSubmitting(false);
    }
  }, [clientError, analytics]);

  useEffect(() => {
    if (serverError && !hasLoggedServerError.current) {
      analytics.track('support_conversations_rac_server_error', {
        universalHelp: true,
      });
      captureException(new Error(serverError.message));
      mostRecentServerError.current = serverError;
      hasLoggedServerError.current = true;
    }

    if (serverError) {
      setSubmitting(false);
    }
  }, [serverError, analytics]);

  useEffect(() => {
    if (isRacFormSubmitted) {
      return;
    }

    const retrieveInitialMessage = async () => {
      const result = await events.emitWithResult({
        name: 'inline-data-channel-form-initial-message',
        data: {},
        nonce,
      });

      if (!result.error && typeof result.data?.initialMessage === 'string') {
        setFormData((prev) => ({
          ...prev,
          message: result.data?.initialMessage as string,
        }));
      }
    };

    retrieveInitialMessage();
  }, [events, isRacFormSubmitted, nonce]);

  const handleRacSubmit = useCallback(
    async (name: string, phoneNumber: string, message: string) => {
      analytics.action('support_conversations_rac_submit');
      mostRecentClientError.current = undefined;
      hasLoggedClientError.current = false;

      setSubmitting(true);

      const data: RequestACallForm = {
        name,
        phone_number: phoneNumber,
        message,
      };

      setFormData(data);

      action({
        selected_option_id: 'channel_rac',
        rac_form: {
          ...data,
          analytics_event_id: await analytics.requestAnalyticsEventId(),
        },
      });
    },
    [action, analytics],
  );

  const handleRacCancel = useCallback(() => {
    setShowInstructions(false);
    setShowForm(false);
    handleCancel();
  }, [handleCancel, setShowForm, setShowInstructions]);

  return (
    <view.div
      css={{
        stack: 'y',
        gap: 'large',
      }}
    >
      <AnimatePresence>
        {showInstructions ? (
          <view.div
            uses={[
              animate.in({
                duration: ANIMATION_DURATION,
                opacity: 0,
                easing: 'cubic-bezier(0.16, 1, 0.3, 1)',
              }),
            ]}
          >
            {/* eslint-disable-next-line react/style-prop-object */}
            <TextRow body="" style="info">
              <FormattedMessage {...messages.requestACallInstructionsMessage} />
            </TextRow>
          </view.div>
        ) : null}
      </AnimatePresence>

      <AnimatePresence>
        {showForm ? (
          <view.div
            uses={[
              animate.in({
                duration: ANIMATION_DURATION,
                opacity: 0,
                easing: 'cubic-bezier(0.16, 1, 0.3, 1)',
              }),
            ]}
          >
            <RequestACallForm
              initialName={formData.name}
              initialPhoneNumber={formData.phone_number}
              initialMessage={formData.message}
              handleSubmit={handleRacSubmit}
              handleCancel={handleRacCancel}
              clientError={
                mostRecentServerError.current
                  ? 'rac-generic'
                  : mostRecentClientError.current
              }
              submitting={submitting}
            />
          </view.div>
        ) : null}
      </AnimatePresence>

      <AnimatePresence>
        {showSummary && requestACallForm ? (
          <view.div
            uses={[
              animate.in({
                duration: ANIMATION_DURATION,
                opacity: 0,
                easing: 'cubic-bezier(0.16, 1, 0.3, 1)',
              }),
            ]}
          >
            <view.div>
              <view.div
                className="uh-conversations-button-options-message-row"
                data-message-type="user"
                css={{
                  margin: 'small',
                }}
              >
                <TextRow
                  position="end"
                  body={`${requestACallForm.name}
            ${requestACallForm.phone_number}
            ${requestACallForm.message}`}
                />
              </view.div>
            </view.div>
          </view.div>
        ) : null}
      </AnimatePresence>

      <AnimatePresence>
        {showExplanation ? (
          <view.div
            uses={[
              animate.in({
                duration: ANIMATION_DURATION,
                opacity: 0,
                easing: 'cubic-bezier(0.16, 1, 0.3, 1)',
              }),
            ]}
          >
            {/* eslint-disable-next-line react/style-prop-object */}
            <TextRow body="" style="info">
              <FormattedMessage {...messages.requestACallExplanationMessage} />
            </TextRow>
          </view.div>
        ) : null}
      </AnimatePresence>
    </view.div>
  );
}
